import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  IconButton,
  Chip,
  OutlinedInput,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaletteIcon from '@mui/icons-material/Palette';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import GridViewIcon from '@mui/icons-material/GridView';
import BoltIcon from '@mui/icons-material/Bolt';
import TuneIcon from '@mui/icons-material/Tune';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useApp } from '../../context/AppContext';
import { themes } from '../../config/themes';
import { updateColors } from '../../config/colors';
import { anos, meses } from '../../data/mockData';
import { getConfig, getConfigs, setConfig } from '../../services/supabaseService';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 1 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function Configuracoes() {
  const { selectedThemeKey, changeTheme, metasPorAno, salvarMeta } = useApp();
  const [tabIndex, setTabIndex] = useState(0);
  const [anoSelecionadoMeta, setAnoSelecionadoMeta] = useState(2026);
  // Estado para armazenar valores do banco (fonte da verdade)
  const [metasDoBanco, setMetasDoBanco] = useState({});
  // Estado para valor da meta anual por ano ('2026': 'R$ 30.000.000,00')
  const [metasAnualValues, setMetasAnualValues] = useState({});
  // Estado para controlar quais campos estão sendo editados
  const [camposEditando, setCamposEditando] = useState({});
  const [nomeSistema, setNomeSistema] = useState('Maratona Prime Class 2026');
  const [spotifyUrl, setSpotifyUrl] = useState('https://open.spotify.com/playlist/05z4UkXubZauJUzUArVEHs');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [soundcloudUrl, setSoundcloudUrl] = useState('');
  const [musicService, setMusicService] = useState('youtube'); // 'spotify', 'youtube', 'soundcloud'
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [salvando, setSalvando] = useState(false);
  // Ref para controlar quais anos tiveram seu tipo alterado pelo usuário nesta sessão
  const userChangedTiposRef = useRef({});

  // Tipo de meta por ano: 'anual' | 'semestral' | 'trimestral' | 'personalizada'
  const [tiposMeta, setTiposMeta] = useState(() => {
    const tipos = {};
    anos.forEach(ano => {
      tipos[ano] = 'anual';
    });
    return tipos;
  });

  // Metas detalhadas por período ('2027_s1', '2027_q1', etc.)
  const [metasDetalhadas, setMetasDetalhadas] = useState({});

  // Metas personalizadas por ano ({ 2026: [ { id, mesInicio, mesFim, valor1, valor2, valor3 } ] })
  const [metasPersonalizadas, setMetasPersonalizadas] = useState({});

  // Carregar detalhamento de metas diretamente do Banco de Dados Supabase ao montar
  useEffect(() => {
    const loadDetailedMetas = async () => {
      const chaves = [];
      anos.forEach(ano => {
        chaves.push(`meta_tipo_${ano}`);
        chaves.push(`meta_anual_${ano}`);
        chaves.push(`meta_${ano}_s1`);
        chaves.push(`meta_${ano}_s2`);
        chaves.push(`meta_${ano}_q1`);
        chaves.push(`meta_${ano}_q2`);
        chaves.push(`meta_${ano}_q3`);
        chaves.push(`meta_${ano}_q4`);
        chaves.push(`meta_personalizada_${ano}`);
      });

      let configs = {};
      try {
        configs = await getConfigs(chaves);
      } catch (e) {
        console.warn('Erro ao carregar detalhamento de metas do Supabase:', e);
      }

      const detalhadas = {};
      const personalizadasCarregadas = {};
      const anuaisCarregadas = {};

      anos.forEach(ano => {
        // Meta Anual isolada
        const valAnual = configs[`meta_anual_${ano}`];
        if (valAnual) {
          const num = parseFloat(valAnual);
          if (!isNaN(num) && num > 0) {
            anuaisCarregadas[ano] = formatarValorMoeda(num);
          }
        } else if (metasPorAno && metasPorAno[ano] > 0 && configs[`meta_tipo_${ano}`] === 'anual') {
          anuaisCarregadas[ano] = formatarValorMoeda(metasPorAno[ano]);
        }

        // Semestres
        for (let s = 1; s <= 2; s++) {
          const key = `${ano}_s${s}`;
          const val = configs[`meta_${key}`];
          if (val) {
            const num = parseFloat(val);
            if (!isNaN(num) && num > 0) {
              detalhadas[key] = formatarValorMoeda(num);
            }
          }
        }

        // Trimestres
        for (let q = 1; q <= 4; q++) {
          const key = `${ano}_q${q}`;
          const val = configs[`meta_${key}`];
          if (val) {
            const num = parseFloat(val);
            if (!isNaN(num) && num > 0) {
              detalhadas[key] = formatarValorMoeda(num);
            }
          }
        }

        // Personalizadas (JSON array)
        const jsonStr = configs[`meta_personalizada_${ano}`];
        if (jsonStr) {
          try {
            const parsed = JSON.parse(jsonStr);
            if (Array.isArray(parsed) && parsed.length > 0) {
              personalizadasCarregadas[ano] = parsed;
            }
          } catch (ePers) {
            console.warn(`Erro ao converter JSON de meta personalizada para ${ano}:`, ePers);
          }
        }
      });

      setTiposMeta(prevTipos => {
        const novosTipos = { ...prevTipos };
        anos.forEach(ano => {
          if (!userChangedTiposRef.current[ano]) {
            const dbTipo = configs[`meta_tipo_${ano}`];
            if (dbTipo) {
              novosTipos[ano] = dbTipo;
            } else if (personalizadasCarregadas[ano] && personalizadasCarregadas[ano].length > 0) {
              novosTipos[ano] = 'personalizada';
            }
          }
        });
        return novosTipos;
      });

      setMetasAnualValues(prev => ({ ...anuaisCarregadas, ...prev }));
      setMetasDetalhadas(prev => ({ ...detalhadas, ...prev }));
      setMetasPersonalizadas(prev => ({ ...personalizadasCarregadas, ...prev }));
    };

    loadDetailedMetas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calcularTotalMetaItem = (item) => {
    if (!item) return 0;
    const v1 = converterStringParaNumero(item.valor1 !== undefined ? item.valor1 : (item.valor || '')) || 0;
    const v2 = converterStringParaNumero(item.valor2 || '') || 0;
    const v3 = converterStringParaNumero(item.valor3 || '') || 0;
    return v1 + v2 + v3;
  };

  const calcularTotalAno = (ano) => {
    const tipo = tiposMeta[ano] || 'anual';
    if (tipo === 'anual') {
      const valStr = metasAnualValues[ano] || '';
      return converterStringParaNumero(valStr) || 0;
    }
    if (tipo === 'semestral') {
      const s1 = converterStringParaNumero(metasDetalhadas[`${ano}_s1`] || '') || 0;
      const s2 = converterStringParaNumero(metasDetalhadas[`${ano}_s2`] || '') || 0;
      return s1 + s2;
    }
    if (tipo === 'trimestral') {
      const q1 = converterStringParaNumero(metasDetalhadas[`${ano}_q1`] || '') || 0;
      const q2 = converterStringParaNumero(metasDetalhadas[`${ano}_q2`] || '') || 0;
      const q3 = converterStringParaNumero(metasDetalhadas[`${ano}_q3`] || '') || 0;
      const q4 = converterStringParaNumero(metasDetalhadas[`${ano}_q4`] || '') || 0;
      return q1 + q2 + q3 + q4;
    }
    if (tipo === 'personalizada') {
      const lista = metasPersonalizadas[ano] || [];
      return lista.reduce((acc, curr) => acc + calcularTotalMetaItem(curr), 0);
    }
    return 0;
  };

  const formatarPeriodoCustom = (item) => {
    const inicio = item.mesInicio || 'Janeiro';
    const fim = item.mesFim || 'Dezembro';
    return inicio === fim ? inicio : `${inicio} a ${fim}`;
  };

  const handleAdicionarMetaPersonalizada = (ano) => {
    setCamposEditando(prev => ({ ...prev, [ano]: true }));
    userChangedTiposRef.current[ano] = true;
    setTiposMeta(prev => ({ ...prev, [ano]: 'personalizada' }));
    const novaMeta = {
      id: Date.now() + Math.random().toString(36).substr(2, 4),
      mesInicio: 'Janeiro',
      mesFim: 'Março',
      valor1: '',
      valor2: '',
      valor3: '',
      periodo: 'Janeiro a Março',
    };
    setMetasPersonalizadas(prev => {
      const listaAtual = prev[ano] || [];
      return { ...prev, [ano]: [...listaAtual, novaMeta] };
    });
  };

  const handleRemoverMetaPersonalizada = (ano, idMeta) => {
    setCamposEditando(prev => ({ ...prev, [ano]: true }));
    setMetasPersonalizadas(prev => {
      const listaAtual = prev[ano] || [];
      return { ...prev, [ano]: listaAtual.filter(m => m.id !== idMeta) };
    });
  };

  const handleAtualizarMetaPersonalizada = (ano, idMeta, campo, valorInput) => {
    setCamposEditando(prev => ({ ...prev, [ano]: true }));
    setMetasPersonalizadas(prev => {
      const listaAtual = prev[ano] || [];
      const novaLista = listaAtual.map(item => {
        if (item.id === idMeta) {
          let valTratado = valorInput;
          if (campo === 'valor1' || campo === 'valor2' || campo === 'valor3' || campo === 'valor') {
            valTratado = aplicarMascaraMoeda(valorInput);
          }
          const itemAtualizado = { ...item, [campo]: valTratado };
          itemAtualizado.periodo = formatarPeriodoCustom(itemAtualizado);
          return itemAtualizado;
        }
        return item;
      });
      return { ...prev, [ano]: novaLista };
    });
  };

  const handleMetaDetalhadaChange = (key, valor, ano) => {
    setCamposEditando(prev => ({ ...prev, [ano]: true }));
    const valorFormatado = aplicarMascaraMoeda(valor);
    setMetasDetalhadas(prev => ({ ...prev, [key]: valorFormatado }));
  };

  const handleTipoMetaChange = (ano, novoTipo) => {
    if (!novoTipo) return;
    userChangedTiposRef.current[ano] = true;
    setTiposMeta(prev => ({ ...prev, [ano]: novoTipo }));
    setCamposEditando(prev => ({ ...prev, [ano]: true }));
  };

  // Load music service and URLs from database on mount
  useEffect(() => {
    const loadMusicConfig = async () => {
      try {
        const savedService = await getConfig('music_service') || 'youtube';
        setMusicService(savedService);

        const spotifyUrlSaved = await getConfig('spotify_url');
        const youtubeUrlSaved = await getConfig('youtube_url');
        const soundcloudUrlSaved = await getConfig('soundcloud_url');

        if (spotifyUrlSaved) setSpotifyUrl(spotifyUrlSaved);
        if (youtubeUrlSaved) setYoutubeUrl(youtubeUrlSaved);
        if (soundcloudUrlSaved) setSoundcloudUrl(soundcloudUrlSaved);
      } catch (error) {
        console.error('Erro ao carregar configuração de música do banco:', error);
      }
    };
    loadMusicConfig();
  }, []);

  // Função para formatar valor como moeda brasileira (R$)
  const formatarValorMoeda = (valor) => {
    if (valor === null || valor === undefined || valor === '') return '';
    const numero = typeof valor === 'number' ? valor : parseFloat(valor);
    if (isNaN(numero)) return '';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numero);
  };

  // Inicializar valores do banco quando metasPorAno mudar (apenas se não estiver editando)
  useEffect(() => {
    const algumCampoEditando = Object.values(camposEditando).some(editando => editando === true);
    if (algumCampoEditando) return;

    if (metasPorAno && Object.keys(metasPorAno).length > 0) {
      let precisaAtualizar = false;
      anos.forEach(ano => {
        const valorBanco = metasPorAno[ano] !== undefined ? metasPorAno[ano] : null;
        const valorAtual = metasDoBanco[ano] !== undefined ? metasDoBanco[ano] : null;
        if (valorBanco !== valorAtual) {
          precisaAtualizar = true;
        }
      });

      if (precisaAtualizar || Object.keys(metasDoBanco).length === 0) {
        setMetasDoBanco(metasPorAno);
        const anuaisAtualizados = {};
        anos.forEach(ano => {
          const valor = metasPorAno[ano] !== undefined && metasPorAno[ano] !== null ? metasPorAno[ano] : null;
          if (tiposMeta[ano] === 'anual' && valor !== null && valor > 0 && !metasAnualValues[ano]) {
            anuaisAtualizados[ano] = formatarValorMoeda(valor);
          }
        });
        if (Object.keys(anuaisAtualizados).length > 0) {
          setMetasAnualValues(prev => ({ ...anuaisAtualizados, ...prev }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metasPorAno]);

  // Função para salvar configurações diretamente no Banco de Dados (Supabase)
  const handleSave = async () => {
    if (salvando) {
      console.log('handleSave: Já está salvando, ignorando clique...');
      return;
    }

    try {
      setSalvando(true);
      console.log('handleSave: Iniciando salvamento no Supabase...');

      // 1. Salvar Metas e Detalhamento (Anual, Semestral, Trimestral) para todos os anos NO SUPABASE
      for (const ano of anos) {
        const tipo = tiposMeta[ano] || 'anual';
        let totalCalculado = 0;

        // Salvar tipo de meta no Supabase
        try {
          await setConfig(`meta_tipo_${ano}`, tipo);
        } catch (eTipo) {
          console.warn(`Erro ao salvar meta_tipo_${ano} no Supabase:`, eTipo);
        }

        if (tipo === 'anual') {
          const valStr = metasAnualValues[ano] || '';
          const numAnual = converterStringParaNumero(valStr) || 0;
          totalCalculado = numAnual;

          // Salvar valor da meta anual no Supabase
          try {
            await setConfig(`meta_anual_${ano}`, numAnual);
          } catch (eAnual) {
            console.warn(`Erro ao salvar meta_anual_${ano} no Supabase:`, eAnual);
          }

          // Zerar periodicidades inativas no Supabase
          try {
            await setConfig(`meta_${ano}_s1`, 0);
            await setConfig(`meta_${ano}_s2`, 0);
            await setConfig(`meta_${ano}_q1`, 0);
            await setConfig(`meta_${ano}_q2`, 0);
            await setConfig(`meta_${ano}_q3`, 0);
            await setConfig(`meta_${ano}_q4`, 0);
            await setConfig(`meta_personalizada_${ano}`, '[]');
          } catch (eZerar) {
            console.warn(`Erro ao zerar periodicidades inativas para ${ano}:`, eZerar);
          }

          // Zerar periodicidades inativas no estado local
          setMetasDetalhadas(prev => {
            const copy = { ...prev };
            delete copy[`${ano}_s1`];
            delete copy[`${ano}_s2`];
            delete copy[`${ano}_q1`];
            delete copy[`${ano}_q2`];
            delete copy[`${ano}_q3`];
            delete copy[`${ano}_q4`];
            return copy;
          });
          setMetasPersonalizadas(prev => ({ ...prev, [ano]: [] }));

        } else if (tipo === 'semestral') {
          const s1Val = metasDetalhadas[`${ano}_s1`] || '';
          const s2Val = metasDetalhadas[`${ano}_s2`] || '';
          const s1Num = converterStringParaNumero(s1Val) || 0;
          const s2Num = converterStringParaNumero(s2Val) || 0;
          totalCalculado = s1Num + s2Num;

          try {
            await setConfig(`meta_${ano}_s1`, s1Num);
            await setConfig(`meta_${ano}_s2`, s2Num);
          } catch (eSem) {
            console.warn(`Erro ao salvar detalhamento semestral para ${ano} no Supabase:`, eSem);
          }

          // Zerar periodicidades inativas no Supabase
          try {
            await setConfig(`meta_anual_${ano}`, 0);
            await setConfig(`meta_${ano}_q1`, 0);
            await setConfig(`meta_${ano}_q2`, 0);
            await setConfig(`meta_${ano}_q3`, 0);
            await setConfig(`meta_${ano}_q4`, 0);
            await setConfig(`meta_personalizada_${ano}`, '[]');
          } catch (eZerar) {
            console.warn(`Erro ao zerar periodicidades inativas para ${ano}:`, eZerar);
          }

          // Zerar periodicidades inativas no estado local
          setMetasAnualValues(prev => ({ ...prev, [ano]: '' }));
          setMetasDetalhadas(prev => {
            const copy = { ...prev };
            delete copy[`${ano}_q1`];
            delete copy[`${ano}_q2`];
            delete copy[`${ano}_q3`];
            delete copy[`${ano}_q4`];
            return copy;
          });
          setMetasPersonalizadas(prev => ({ ...prev, [ano]: [] }));

        } else if (tipo === 'trimestral') {
          const q1Val = metasDetalhadas[`${ano}_q1`] || '';
          const q2Val = metasDetalhadas[`${ano}_q2`] || '';
          const q3Val = metasDetalhadas[`${ano}_q3`] || '';
          const q4Val = metasDetalhadas[`${ano}_q4`] || '';

          const q1Num = converterStringParaNumero(q1Val) || 0;
          const q2Num = converterStringParaNumero(q2Val) || 0;
          const q3Num = converterStringParaNumero(q3Val) || 0;
          const q4Num = converterStringParaNumero(q4Val) || 0;
          totalCalculado = q1Num + q2Num + q3Num + q4Num;

          try {
            await setConfig(`meta_${ano}_q1`, q1Num);
            await setConfig(`meta_${ano}_q2`, q2Num);
            await setConfig(`meta_${ano}_q3`, q3Num);
            await setConfig(`meta_${ano}_q4`, q4Num);
          } catch (eTri) {
            console.warn(`Erro ao salvar detalhamento trimestral para ${ano} no Supabase:`, eTri);
          }

          // Zerar periodicidades inativas no Supabase
          try {
            await setConfig(`meta_anual_${ano}`, 0);
            await setConfig(`meta_${ano}_s1`, 0);
            await setConfig(`meta_${ano}_s2`, 0);
            await setConfig(`meta_personalizada_${ano}`, '[]');
          } catch (eZerar) {
            console.warn(`Erro ao zerar periodicidades inativas para ${ano}:`, eZerar);
          }

          // Zerar periodicidades inativas no estado local
          setMetasAnualValues(prev => ({ ...prev, [ano]: '' }));
          setMetasDetalhadas(prev => {
            const copy = { ...prev };
            delete copy[`${ano}_s1`];
            delete copy[`${ano}_s2`];
            return copy;
          });
          setMetasPersonalizadas(prev => ({ ...prev, [ano]: [] }));

        } else if (tipo === 'personalizada') {
          const lista = metasPersonalizadas[ano] || [];
          totalCalculado = lista.reduce((acc, curr) => acc + calcularTotalMetaItem(curr), 0);

          try {
            await setConfig(`meta_personalizada_${ano}`, JSON.stringify(lista));
          } catch (ePers) {
            console.warn(`Erro ao salvar meta personalizada para ${ano} no Supabase:`, ePers);
          }

          // Zerar periodicidades inativas no Supabase
          try {
            await setConfig(`meta_anual_${ano}`, 0);
            await setConfig(`meta_${ano}_s1`, 0);
            await setConfig(`meta_${ano}_s2`, 0);
            await setConfig(`meta_${ano}_q1`, 0);
            await setConfig(`meta_${ano}_q2`, 0);
            await setConfig(`meta_${ano}_q3`, 0);
            await setConfig(`meta_${ano}_q4`, 0);
          } catch (eZerar) {
            console.warn(`Erro ao zerar periodicidades inativas para ${ano}:`, eZerar);
          }

          // Zerar periodicidades inativas no estado local
          setMetasAnualValues(prev => ({ ...prev, [ano]: '' }));
          setMetasDetalhadas(prev => {
            const copy = { ...prev };
            delete copy[`${ano}_s1`];
            delete copy[`${ano}_s2`];
            delete copy[`${ano}_q1`];
            delete copy[`${ano}_q2`];
            delete copy[`${ano}_q3`];
            delete copy[`${ano}_q4`];
            return copy;
          });
        }

        // Salvar a meta total do ano no Supabase e AppContext
        try {
          await salvarMeta(ano, totalCalculado);
        } catch (eMeta) {
          console.warn(`Erro ao salvar meta total para ${ano} no Supabase:`, eMeta);
        }
      }

      setCamposEditando({});

      // 2. Salvar outras configurações diretamente no Supabase
      try {
        await setConfig('nome_sistema', nomeSistema);
        await setConfig('music_service', musicService);
        await setConfig('spotify_url', spotifyUrl);
        await setConfig('youtube_url', youtubeUrl);
        await setConfig('soundcloud_url', soundcloudUrl);
      } catch (eOutros) {
        console.warn('Erro ao salvar configurações gerais no Supabase:', eOutros);
      }

      // Disparar evento para notificar player de áudio se necessário
      window.dispatchEvent(new Event('musicUrlUpdated'));

      console.log('handleSave: Salvamento concluído no banco de dados Supabase');

      setSnackbarMessage('Configurações salvas com sucesso!');
      setSnackbarOpen(true);

      // Disparar eventos de atualização para atualizar Dashboard e Player
      window.dispatchEvent(new Event('metaUpdated'));
      window.dispatchEvent(new Event('themeChanged'));
    } catch (error) {
      console.error('Error saving configurations:', error);
      setSnackbarMessage('Erro ao salvar as configurações. Tente novamente.');
      setSnackbarOpen(true);
    } finally {
      setSalvando(false);
    }
  };

  // Função para converter string formatada em número
  const converterStringParaNumero = (valorString) => {
    if (!valorString || valorString.trim() === '') return null;
    // Remove tudo exceto números
    const apenasNumeros = valorString.replace(/\D/g, '');
    if (apenasNumeros === '') return null;
    // Divide por 100 para considerar centavos
    return parseFloat(apenasNumeros) / 100;
  };

  // Função para aplicar máscara de moeda durante a digitação
  const aplicarMascaraMoeda = (valor) => {
    // Remove tudo exceto números
    const apenasNumeros = valor.replace(/\D/g, '');

    // Se não houver números, retorna vazio
    if (apenasNumeros === '') return '';

    // Converte para número e divide por 100 para ter centavos
    const numero = parseFloat(apenasNumeros) / 100;

    // Formata como moeda brasileira
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numero);
  };

  const handleMetaChange = (ano, valor) => {
    setCamposEditando(prev => ({
      ...prev,
      [ano]: true
    }));

    const valorFormatado = aplicarMascaraMoeda(valor);

    setMetasAnualValues(prev => ({
      ...prev,
      [ano]: valorFormatado
    }));
  };

  const handleMetaBlur = (ano) => {
    const valorString = metasAnualValues[ano] || '';

    if (valorString.trim() !== '') {
      const valorNumerico = converterStringParaNumero(valorString);
      if (valorNumerico !== null && !isNaN(valorNumerico) && valorNumerico >= 0) {
        setMetasAnualValues(prev => ({
          ...prev,
          [ano]: formatarValorMoeda(valorNumerico)
        }));
      }
    }
  };

  const handleMetaFocus = (ano) => {
    // Quando ganha foco, marca como editando
    setCamposEditando(prev => ({
      ...prev,
      [ano]: true
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
        Configurações
      </Typography>

      {/* Tabs Navigation Header */}
      <Paper
        elevation={3}
        sx={{
          mb: 3,
          borderRadius: 3,
          background: (theme) => theme.palette.mode === 'dark'
            ? '#1818188e'
            : '#ffffff',
          border: '1px solid rgba(210, 179, 92, 0.3)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(e, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons={false}
          TabIndicatorProps={{
            style: {
              height: '4px',
              borderRadius: '4px 4px 0 0',
              background: 'linear-gradient(90deg, #B59543 0%, #D2B35C 50%, #F5E19A 100%)',
              boxShadow: '0 0 12px rgba(210, 179, 92, 0.8)',
            },
          }}
          sx={{
            px: 2,
            pt: 1,
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            '& .MuiTabs-scrollButtons': {
              display: 'none !important',
              width: 0,
            },
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              py: 2,
              px: 3,
              minHeight: 58,
              borderRadius: '10px 10px 0 0',
              color: 'text.secondary',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              mr: 1,
              '&:hover': {
                color: '#D2B35C',
                backgroundColor: 'rgba(210, 179, 92, 0.08)',
                transform: 'translateY(-1px)',
              },
              '&.Mui-selected': {
                color: '#D2B35C',
                fontWeight: 700,
                backgroundColor: 'rgba(210, 179, 92, 0.15)',
                backdropFilter: 'blur(4px)',
                '& .MuiSvgIcon-root': {
                  color: '#D2B35C',
                  filter: 'drop-shadow(0 0 6px rgba(210, 179, 92, 0.6))',
                },
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.3rem',
                transition: 'all 0.3s ease',
              },
            },
          }}
        >
          <Tab icon={<EmojiEventsIcon />} iconPosition="start" label="Metas" />
          <Tab icon={<MusicNoteIcon />} iconPosition="start" label="Playlist" />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Ajustes do Sistema" />
          <Tab icon={<PaletteIcon />} iconPosition="start" label="Tema" />
        </Tabs>
      </Paper>

      {/* Tab 0: Metas */}
      <CustomTabPanel value={tabIndex} index={0}>
        <Paper sx={{ p: 3 }}>

          {/* Subtabs Seletores de Ano por Abas */}
          <Paper
            elevation={2}
            sx={{
              mb: 3.5,
              borderRadius: 3,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(25, 25, 30, 0.7)' : '#f8f9fa',
              border: '1px solid rgba(210, 179, 92, 0.25)',
              overflow: 'hidden',
            }}
          >
            <Tabs
              value={anoSelecionadoMeta}
              onChange={(e, newAno) => setAnoSelecionadoMeta(newAno)}
              variant="fullWidth"
              TabIndicatorProps={{
                style: {
                  height: '3px',
                  borderRadius: '3px 3px 0 0',
                  backgroundColor: '#D2B35C',
                  boxShadow: '0 0 10px rgba(210, 179, 92, 0.8)',
                },
              }}
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 700,
                  fontSize: '0.92rem',
                  textTransform: 'none',
                  py: 1.8,
                  color: 'text.secondary',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    color: '#D2B35C',
                    backgroundColor: 'rgba(210, 179, 92, 0.08)',
                  },
                  '&.Mui-selected': {
                    color: '#D2B35C',
                    fontWeight: 800,
                    backgroundColor: 'rgba(210, 179, 92, 0.12)',
                  },
                },
              }}
            >
              {anos.map((ano) => (
                <Tab
                  key={ano}
                  value={ano}
                  label={`Ano ${ano}`}
                />
              ))}
            </Tabs>
          </Paper>

          {/* Card do Ano Selecionado */}
          {(() => {
            const ano = anoSelecionadoMeta;
            const tipo = tiposMeta[ano] || 'anual';
            const totalCalculado = calcularTotalAno(ano);

            return (
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  border: '1.5px solid rgba(210, 179, 92, 0.35)',
                  background: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 30, 35, 0.6)' : '#fcfcfc',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Top Header Row: Periodicidade (Esquerda) + Total Acumulado (Direita) */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                    {/* Seletor de Periodicidade */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Periodicidade:
                      </Typography>
                      <ToggleButtonGroup
                        value={tipo}
                        exclusive
                        onChange={(e, newTipo) => handleTipoMetaChange(ano, newTipo)}
                        size="small"
                        sx={{
                          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.04)',
                          p: '3px',
                          borderRadius: '14px',
                          border: '1px solid rgba(210, 179, 92, 0.3)',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '3px',
                          '& .MuiToggleButtonGroup-grouped': {
                            border: 'none !important',
                            borderRadius: '10px !important',
                          },
                          '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 2,
                            py: 0.8,
                            fontSize: '0.85rem',
                            color: 'text.secondary',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(210, 179, 92, 0.1)',
                              color: '#D2B35C',
                            },
                            '&.Mui-selected': {
                              color: '#D2B35C',
                              backgroundColor: 'rgba(210, 179, 92, 0.22)',
                              boxShadow: '0 2px 8px rgba(210, 179, 92, 0.25)',
                              fontWeight: 800,
                            }
                          }
                        }}
                      >
                        <ToggleButton value="anual" sx={{ display: 'flex', gap: 0.6, alignItems: 'center' }}>
                          <CalendarTodayIcon sx={{ fontSize: '1rem' }} /> Anual
                        </ToggleButton>
                        <ToggleButton value="semestral" sx={{ display: 'flex', gap: 0.6, alignItems: 'center' }}>
                          <DateRangeIcon sx={{ fontSize: '1rem' }} /> Semestral
                        </ToggleButton>
                        <ToggleButton value="trimestral" sx={{ display: 'flex', gap: 0.6, alignItems: 'center' }}>
                          <GridViewIcon sx={{ fontSize: '1rem' }} /> Trimestral
                        </ToggleButton>
                        <ToggleButton value="personalizada" sx={{ display: 'flex', gap: 0.6, alignItems: 'center' }}>
                          <TuneIcon sx={{ fontSize: '1rem' }} /> Personalizada
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Badge Elegante de Total Calculado */}
                    <Paper
                      elevation={0}
                      sx={{
                        px: 2.5,
                        py: 1,
                        borderRadius: 2.5,
                        background: 'linear-gradient(135deg, rgba(210, 179, 92, 0.18) 0%, rgba(210, 179, 92, 0.06) 100%)',
                        border: '1px solid rgba(210, 179, 92, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <BoltIcon sx={{ color: '#D2B35C', fontSize: '1.5rem' }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', lineHeight: 1 }}>
                          Total Acumulado ({ano})
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#D2B35C', lineHeight: 1.2 }}>
                          {totalCalculado > 0 ? formatarValorMoeda(totalCalculado) : 'R$ 0,00'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>

                  <Divider sx={{ mb: 3, borderColor: 'rgba(210, 179, 92, 0.15)' }} />

                  {/* Campos dinâmicos baseados no tipo */}
                  {tipo === 'anual' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={`Meta Anual ${ano}`}
                          type="text"
                          value={metasAnualValues[ano] || ''}
                          onChange={(e) => handleMetaChange(ano, e.target.value)}
                          onBlur={() => handleMetaBlur(ano)}
                          onFocus={() => handleMetaFocus(ano)}
                          variant="outlined"
                          placeholder="R$ 0,00"
                          helperText="Valor total da meta de VGV para o ano completo"
                          inputProps={{ inputMode: 'numeric' }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {tipo === 'semestral' && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="1º Semestre (Jan - Jun)"
                          type="text"
                          value={metasDetalhadas[`${ano}_s1`] || ''}
                          onChange={(e) => handleMetaDetalhadaChange(`${ano}_s1`, e.target.value, ano)}
                          variant="outlined"
                          placeholder="R$ 0,00"
                          inputProps={{ inputMode: 'numeric' }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="2º Semestre (Jul - Dez)"
                          type="text"
                          value={metasDetalhadas[`${ano}_s2`] || ''}
                          onChange={(e) => handleMetaDetalhadaChange(`${ano}_s2`, e.target.value, ano)}
                          variant="outlined"
                          placeholder="R$ 0,00"
                          inputProps={{ inputMode: 'numeric' }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {tipo === 'trimestral' && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          label="1º Trimestre (Jan - Mar)"
                          type="text"
                          value={metasDetalhadas[`${ano}_q1`] || ''}
                          onChange={(e) => handleMetaDetalhadaChange(`${ano}_q1`, e.target.value, ano)}
                          variant="outlined"
                          placeholder="R$ 0,00"
                          inputProps={{ inputMode: 'numeric' }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          label="2º Trimestre (Abr - Jun)"
                          type="text"
                          value={metasDetalhadas[`${ano}_q2`] || ''}
                          onChange={(e) => handleMetaDetalhadaChange(`${ano}_q2`, e.target.value, ano)}
                          variant="outlined"
                          placeholder="R$ 0,00"
                          inputProps={{ inputMode: 'numeric' }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          label="3º Trimestre (Jul - Set)"
                          type="text"
                          value={metasDetalhadas[`${ano}_q3`] || ''}
                          onChange={(e) => handleMetaDetalhadaChange(`${ano}_q3`, e.target.value, ano)}
                          variant="outlined"
                          placeholder="R$ 0,00"
                          inputProps={{ inputMode: 'numeric' }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          label="4º Trimestre (Out - Dez)"
                          type="text"
                          value={metasDetalhadas[`${ano}_q4`] || ''}
                          onChange={(e) => handleMetaDetalhadaChange(`${ano}_q4`, e.target.value, ano)}
                          variant="outlined"
                          placeholder="R$ 0,00"
                          inputProps={{ inputMode: 'numeric' }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {tipo === 'personalizada' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {(metasPersonalizadas[ano] || []).length === 0 ? (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 3,
                            textAlign: 'center',
                            borderRadius: 2.5,
                            borderStyle: 'dashed',
                            borderColor: 'rgba(210, 179, 92, 0.4)',
                            backgroundColor: 'rgba(210, 179, 92, 0.03)',
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                            Nenhuma meta personalizada para {ano}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Clique em "+ Adicionar Período" abaixo para cadastrar períodos customizados.
                          </Typography>
                        </Paper>
                      ) : (
                        (metasPersonalizadas[ano] || []).map((metaItem, idx) => (
                          <Paper
                            key={metaItem.id || idx}
                            variant="outlined"
                            sx={{
                              p: 2.5,
                              borderRadius: 3,
                              borderColor: 'rgba(210, 179, 92, 0.25)',
                              background: (theme) =>
                                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.8 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#D2B35C' }}>
                                Período #{idx + 1} ({formatarPeriodoCustom(metaItem)})
                              </Typography>
                              <IconButton
                                onClick={() => handleRemoverMetaPersonalizada(ano, metaItem.id)}
                                color="error"
                                size="small"
                                title="Remover Período"
                                sx={{
                                  border: '1px solid rgba(244, 67, 54, 0.3)',
                                  borderRadius: 2,
                                  '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' },
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Mês Inicial</InputLabel>
                                  <Select
                                    value={metaItem.mesInicio || 'Janeiro'}
                                    label="Mês Inicial"
                                    onChange={(e) => handleAtualizarMetaPersonalizada(ano, metaItem.id, 'mesInicio', e.target.value)}
                                    sx={{ borderRadius: '12px' }}
                                  >
                                    {meses.map((m) => (
                                      <MenuItem key={m} value={m}>{m}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Mês Final</InputLabel>
                                  <Select
                                    value={metaItem.mesFim || 'Março'}
                                    label="Mês Final"
                                    onChange={(e) => handleAtualizarMetaPersonalizada(ano, metaItem.id, 'mesFim', e.target.value)}
                                    sx={{ borderRadius: '12px' }}
                                  >
                                    {meses.map((m) => (
                                      <MenuItem key={m} value={m}>{m}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} sm={4} md={2}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Meta 1 (R$)"
                                  value={metaItem.valor1 !== undefined ? metaItem.valor1 : (metaItem.valor || '')}
                                  onChange={(e) => handleAtualizarMetaPersonalizada(ano, metaItem.id, 'valor1', e.target.value)}
                                  placeholder="R$ 0,00"
                                  variant="outlined"
                                  inputProps={{ inputMode: 'numeric' }}
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                />
                              </Grid>

                              <Grid item xs={12} sm={4} md={2}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Meta 2 (R$)"
                                  value={metaItem.valor2 || ''}
                                  onChange={(e) => handleAtualizarMetaPersonalizada(ano, metaItem.id, 'valor2', e.target.value)}
                                  placeholder="Opcional"
                                  variant="outlined"
                                  inputProps={{ inputMode: 'numeric' }}
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                />
                              </Grid>

                              <Grid item xs={12} sm={4} md={2}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Meta 3 (R$)"
                                  value={metaItem.valor3 || ''}
                                  onChange={(e) => handleAtualizarMetaPersonalizada(ano, metaItem.id, 'valor3', e.target.value)}
                                  placeholder="Opcional"
                                  variant="outlined"
                                  inputProps={{ inputMode: 'numeric' }}
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                />
                              </Grid>
                            </Grid>
                          </Paper>
                        ))
                      )}

                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => handleAdicionarMetaPersonalizada(ano)}
                          sx={{
                            borderRadius: 2.5,
                            fontWeight: 700,
                            textTransform: 'none',
                            color: '#D2B35C',
                            borderColor: 'rgba(210, 179, 92, 0.5)',
                            px: 2.5,
                            py: 1,
                            '&:hover': {
                              borderColor: '#D2B35C',
                              backgroundColor: 'rgba(210, 179, 92, 0.1)',
                            },
                          }}
                        >
                          Adicionar Período
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </Paper>
      </CustomTabPanel>

      {/* Tab 1: Playlist */}
      <CustomTabPanel value={tabIndex} index={1}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Configurações de Playlist e Música
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Escolha o serviço e configure as URLs de mídia que serão reproduzidas no Dashboard
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Serviço de Música</InputLabel>
                <Select
                  value={musicService}
                  onChange={(e) => setMusicService(e.target.value)}
                  label="Serviço de Música"
                >
                  <MenuItem value="youtube">YouTube Music</MenuItem>
                  <MenuItem value="soundcloud">SoundCloud</MenuItem>
                  <MenuItem value="spotify">Spotify</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {musicService === 'youtube' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL do YouTube (Vídeo/Playlist)"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  variant="outlined"
                  placeholder="https://www.youtube.com/watch?v=... ou https://www.youtube.com/playlist?list=..."
                  helperText="URL do vídeo ou playlist do YouTube que será reproduzida no Dashboard"
                />
              </Grid>
            )}

            {musicService === 'soundcloud' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL do SoundCloud (Música/Playlist)"
                  value={soundcloudUrl}
                  onChange={(e) => setSoundcloudUrl(e.target.value)}
                  variant="outlined"
                  placeholder="https://soundcloud.com/..."
                  helperText="URL da música ou playlist do SoundCloud que será reproduzida no Dashboard"
                />
              </Grid>
            )}

            {musicService === 'spotify' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL do Spotify (Playlist/Música)"
                  value={spotifyUrl}
                  onChange={(e) => setSpotifyUrl(e.target.value)}
                  variant="outlined"
                  placeholder="https://open.spotify.com/playlist/..."
                  helperText="URL da playlist ou música do Spotify que será reproduzida no Dashboard"
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </CustomTabPanel>

      {/* Tab 2: Ajustes do Sistema */}
      <CustomTabPanel value={tabIndex} index={2}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Ajustes do Sistema
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configurações gerais e parâmetros da aplicação
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome do Sistema"
                value={nomeSistema}
                onChange={(e) => setNomeSistema(e.target.value)}
                variant="outlined"
                helperText="Nome principal exibido na aplicação e relatórios"
              />
            </Grid>
          </Grid>
        </Paper>
      </CustomTabPanel>

      {/* Tab 3: Tema */}
      <CustomTabPanel value={tabIndex} index={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Tema de Cores
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Escolha um tema de cores para personalizar a aparência do sistema
          </Typography>
          <Grid container spacing={2.5}>
            {Object.entries(themes).map(([key, theme]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Card
                  sx={{
                    position: 'relative',
                    border: selectedThemeKey === key ? 2 : 1,
                    borderColor: selectedThemeKey === key
                      ? theme.colors.primary
                      : 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: theme.colors.primary,
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardActionArea onClick={() => changeTheme(key)}>
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {theme.name}
                        </Typography>
                        {selectedThemeKey === key && (
                          <CheckCircleIcon
                            sx={{ color: theme.colors.primary }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {theme.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            bgcolor: theme.colors.primary,
                          }}
                        />
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            bgcolor: theme.colors.secondary,
                          }}
                        />
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            bgcolor: theme.colors.background.paper,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </CustomTabPanel>

      {/* Botão de Salvar Global */}
      {/* Botão de Salvar Global com Efeito Vibrante e Loading */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={
            salvando ? (
              <CircularProgress size={20} sx={{ color: '#000000' }} />
            ) : (
              <SaveIcon sx={{ color: '#000000' }} />
            )
          }
          onClick={handleSave}
          disabled={salvando}
          sx={{
            px: 4,
            py: 1.4,
            borderRadius: 2.5,
            fontWeight: 800,
            fontSize: '0.95rem',
            textTransform: 'none',
            letterSpacing: '0.3px',
            color: '#000000',
            background: 'linear-gradient(135deg, #B59543 0%, #D2B35C 50%, #F5E19A 100%)',
            boxShadow: '0 6px 20px rgba(210, 179, 92, 0.4)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: 'linear-gradient(135deg, #C4A34D 0%, #E3C46C 50%, #FFF2C2 100%)',
              boxShadow: '0 8px 24px rgba(210, 179, 92, 0.6)',
              transform: 'translateY(-2px)',
            },
            '&.Mui-disabled': {
              background: 'linear-gradient(135deg, #D2B35C 0%, #F5E19A 100%)',
              color: '#000000',
              opacity: 0.9,
              boxShadow: '0 4px 16px rgba(210, 179, 92, 0.3)',
            }
          }}
        >
          {salvando ? 'Salvando...' : 'Salvar Configurações'}
        </Button>
      </Box>

      {/* Snackbar para feedback de salvamento */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '28px !important' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarMessage.includes('Erro') ? 'error' : 'success'}
          icon={snackbarMessage.includes('Erro') ? undefined : <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '1.4rem' }} />}
          sx={{
            width: '100%',
            borderRadius: 3,
            px: 3,
            py: 1.3,
            fontWeight: 700,
            fontSize: '0.95rem',
            color: '#ffffff',
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(20, 50, 30, 0.95)' : '#1b5e20',
            border: '1.5px solid #4CAF50',
            boxShadow: '0 8px 32px rgba(76, 175, 80, 0.35)',
            backdropFilter: 'blur(10px)',
            alignItems: 'center',
            '& .MuiAlert-icon': {
              alignItems: 'center',
              mr: 1.5,
            },
            '& .MuiAlert-action': {
              pt: 0,
              color: '#ffffff',
            }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Configuracoes;

